import MainBlock from "components/DesignSystem/LayoutBlocks/MainBlock";
import React, { useEffect, useCallback, useContext, useReducer } from "react";
import { useAuth } from "hooks/Auth";
import { useParams } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { apiGet, apiPost, apiPut, messageFromException } from "helpers/Request";
import {
    transform as containerTransformer,
    ContainerObjectSchema,
    prepare as containerPreparer,
} from "schemas/Container";
import EditForm from "./EditForm";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { toast } from "react-hot-toast";
import { transform } from "lodash";
import { ContainerContext } from "context/Container";
import { AppContext } from "context/App";
import { modal } from "components/HeadlessModal";
import { addItem as addRecentItem } from "redux/actions/recent-items";

const reducer = (oldState, newState) => {
    return Object.assign({}, oldState, newState);
};

const ensureContainerShape = (container, contentFlow) => {
    const allProps = contentFlow.metaProperties["container"];

    let workflow = null;
    if (container.placeholder && container.placeholder?.workflow?.id) {
        workflow = contentFlow.workflows.find(
            (wf) => wf.id === container.placeholder.workflow.id
        );
    }

    if (!workflow) {
        workflow = contentFlow.workflows[0];
    }

    return Object.assign({}, container, {
        meta: transform(
            allProps,
            (result, metaProp, key) => {
                const val = container.metaProperties.find(
                    (mp) => mp.field === metaProp.field
                );
                result[metaProp.field] = val ? val.value : "";
            },
            {}
        ),
        tags: container.tags.map((tag) => {
            return { label: tag.name, value: tag.uuid };
        }),
        workflow: {
            value: workflow.id,
            label: workflow.name,
        },
        sort_order:
            container.sort_order.length &&
            typeof container.sort_order[0] !== "object"
                ? container.sort_order
                : [],
        content: container.content.map((content) => {
            return { uuid: content.uuid };
        }),
        type: "gallery",
    });
};

const prepareForSave = (values, container, contentFlow) => {
    const allProps = contentFlow.metaProperties["container"];

    const tags = values.tags.map((tag) => tag.value);
    const content = values.content.map((content) => content.uuid);

    const meta = Object.keys(values.meta)
        .map((key, i) => {
            const type = allProps.find((mp) => mp.field === key);
            return {
                field: key,
                value: values.meta[key],
                type: type ? type.type : "NoopMetaProperty",
            };
        })
        .filter((mp) => mp.value !== "");

    return containerPreparer(
        Object.assign({}, container, values, {
            tags: tags,
            meta: meta,
            workflow_id: values.workflow.value,
            content: content,
            layout_template: null,
        })
    );
};

const loadContainer = async (uuid, auth, setState) => {
    return apiGet("/v4/container/" + uuid, auth, {
        include: "content,attachment,placeholder",
    })
        .then((response) => {
            return containerTransformer(response);
        })
        .then((item) => {
            setState({ container: item, loading: false });
            return item;
        });
};

export default function GalleryPage(props) {
    const { uuid } = useParams();
    const location = useLocation();
    const auth = useAuth();
    const history = useHistory();
    const dispatch = useDispatch();

    const [state, setState] = useReducer(reducer, {
        container: Object.assign(ContainerObjectSchema, { type: "gallery" }),
        formValues: ContainerObjectSchema,
        loading: false,
        saving: false,
        isUserEditable: false,
        preformingStateUpdate: false,
        requestReload: useCallback(() => {
            loadContainer(uuid, auth.publication, setState).catch((err) => {
                modal.error({
                    title: "Unable to load gallery",
                    message: messageFromException(err),
                });
            });
        }, [uuid, auth.publication]),
    });

    const { setCurrentNav } = useContext(AppContext);

    const contentFlow = useSelector((state) => state.contentFlow);

    const onSubmit = (values) => {
        if (
            state.container.lock &&
            state.container.lock.user.uuid !== auth.user.uuid
        ) {
            console.log("not locked by user");
            return;
        } else if (state.container.uuid && !state.container.lock) {
            console.log("no container lock");
            return;
        }

        const data = prepareForSave(values, state.container, contentFlow);
        setState({ saving: true });

        const apiAction = state.container.uuid ? apiPut : apiPost;
        const id = toast.loading("Saving...");

        return apiAction(
            "/v4/container/" +
                (state.container.uuid ? state.container.uuid : "") +
                "?include=content,attachment,placeholder",
            auth.publication,
            data
        )
            .then((resp) => {
                const item = containerTransformer(resp);
                toast.success("Gallery saved", { id });

                if (!state.container.uuid) {
                    // was a new item, so redirect
                    apiPost("/v4/lock-request", auth.publication, {
                        srn: item.srn,
                    })
                        .then((resp) => {
                            history.push("/app/gallery/" + item.uuid);
                        })
                        .catch((err) => {
                            modal.error({
                                title: "Unable to lock item",
                                message: messageFromException(err),
                            });
                        });
                }

                setState({ container: item, saving: false, loading: false });
            })
            .catch((err) => {
                setState({ saving: false });
                toast.dismiss(id);
                modal.error({
                    title: "Unable to save gallery",
                    message: messageFromException(err),
                });
            });
    };

    const onRequestNavigate = (url) => {
        history.push(url);
    };

    useEffect(() => {
        setState({ loading: true, container: ContainerObjectSchema });
        if (!uuid || uuid === "new") {
            setTimeout(() => {
                setState({ loading: false });
            }, 0);
            return;
        }

        loadContainer(uuid, auth.publication, setState)
            .then((item) => {
                dispatch(addRecentItem(item));
            })
            .catch((err) => {
                console.error(err);
                modal.error({
                    title: "Unable to load gallery",
                    message: messageFromException(err),
                });
            });
    }, [location, uuid, auth.publication, dispatch]);

    useEffect(() => {
        // this is a gallery, so it's "content" not a "container"
        setCurrentNav("content");
    }, [setCurrentNav]);

    useEffect(() => {
        // fixes a bug where the console notes a component updating another, unmounted
        // component. just delay the update until the next tick
        setTimeout(() => {
            setState({
                formValues: ensureContainerShape(state.container, contentFlow),
            });
        });
    }, [state.container, contentFlow]);

    useEffect(() => {
        if (
            state.container.uuid &&
            state.container.lock &&
            state.container.lock.user.uuid === auth.user.uuid
        ) {
            setState({ isUserEditable: true });
        } else if (!state.container.uuid) {
            setState({ isUserEditable: true });
        } else {
            setState({ isUserEditable: false });
        }
    }, [state.container, auth.user]);

    // content and form values are provided via context provider
    return (
        <ContainerContext.Provider
            value={{
                state,
                setState,
            }}
        >
            <MainBlock className="z-0">
                <EditForm
                    onSubmit={onSubmit}
                    onRequestNavigate={onRequestNavigate}
                />
            </MainBlock>
        </ContainerContext.Provider>
    );
}
